import { useI18n } from '@ecom/i18n';
import { Button, Field, Input } from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { useForgotPasswordMutation } from '../api';
import { AuthLayout } from '../components/AuthLayout';
import { StatusIcon } from '../components/StatusIcon';
import { translateFieldError } from '../field-error';
import { forgotPasswordFormSchema, type ForgotPasswordFormValues } from '../schemas';

import { emailRecoveryEnabled } from '@/lib/env';
import { whenDefined } from '@/lib/optional-props';

/**
 * « Mot de passe oublié ? ». While the platform sends no e-mail (the setting `VITE_EMAIL_RECOVERY_ENABLED` is off, the default) the
 * screen does not promise one: it says that no e-mail is sent and who to ask - the platform contact hands over a new provisional
 * password in person - and shows no address field at all (nothing here can reveal whether an account exists). With e-mail recovery
 * enabled the original screen comes back unchanged.
 */
export function ForgotPasswordPage(): ReactElement {
  return emailRecoveryEnabled() ? <ForgotByEmail /> : <ForgotByPlatformContact />;
}

function ForgotByPlatformContact(): ReactElement {
  const { t } = useI18n();
  return (
    <AuthLayout
      icon={<StatusIcon kind="lock" />}
      title={t('auth.forgot.title')}
      subtitle={t('auth.forgot.noEmailSubtitle')}
      footer={
        <Link to="/login" className="font-medium text-accent hover:underline">
          {t('auth.forgot.back')}
        </Link>
      }
    >
      <p className="text-sm text-inkSoft">{t('auth.forgot.noEmailBody')}</p>
    </AuthLayout>
  );
}

function ForgotByEmail(): ReactElement {
  const { t, locale } = useI18n();
  const forgotMutation = useForgotPasswordMutation();
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({ resolver: zodResolver(forgotPasswordFormSchema) });

  async function onSubmit(values: ForgotPasswordFormValues): Promise<void> {
    // The API answers 202 whether or not the address has an account (auth.controller.ts). This page must not distinguish
    // the two cases either — the confirmation screen below is shown on ANY resolution of this call, success or network
    // error alike, never gated on the result.
    try {
      await forgotMutation.mutateAsync({ ...values, locale });
    } finally {
      setSubmitted(true);
    }
  }

  if (submitted) {
    return (
      <AuthLayout
        icon={<StatusIcon kind="mail" />}
        title={t('auth.forgot.sentTitle')}
        subtitle={t('auth.forgot.sentSubtitle')}
        footer={
          <Link to="/login" className="font-medium text-accent hover:underline">
            {t('auth.forgot.back')}
          </Link>
        }
      >
        <p className="text-sm text-inkFaint">{t('auth.forgot.sentHint')}</p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.forgot.title')}
      subtitle={t('auth.forgot.subtitle')}
      footer={
        <Link to="/login" className="font-medium text-accent hover:underline">
          {t('auth.forgot.back')}
        </Link>
      }
    >
      <form
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
        noValidate
        className="flex flex-col gap-4"
      >
        <Field
          label={t('auth.email.label')}
          {...whenDefined('error', translateFieldError(t, errors.email?.message))}
        >
          {(bind) => (
            <Input
              {...bind}
              {...register('email')}
              type="email"
              dir="ltr"
              autoComplete="email"
              autoFocus
            />
          )}
        </Field>

        <Button type="submit" loading={forgotMutation.isPending} className="mt-1 w-full">
          {t('auth.forgot.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}
